import React, { useState, useEffect } from 'react';
import CreateMeetingForm from './components/CreateMeetingForm.tsx';
import ScheduledMeetingsList from './components/ScheduledMeetingsList.tsx';
import { Calendar, Plus, Video, Clock } from 'lucide-react';
import PageHeader, { pageHeaderPrimaryButtonClass } from '../dashboard/components/PageHeader.tsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { meetingService, Meeting } from '../../services/meeting.service.ts';
import { getDefaultMeetingTime } from '../calendar/utils/calendarUtils.ts';
import { filterUpcomingMeetings } from '../meeting/utils/meetingListFilters.ts';

function Plans() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [prefillDate, setPrefillDate] = useState<string | undefined>();
  const [prefillTime, setPrefillTime] = useState<string | undefined>();
  const [returnToCalendar, setReturnToCalendar] = useState(false);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const data = await meetingService.getMeetings();
      setMeetings(filterUpcomingMeetings(data));
    } catch (error) {
      console.error('Toplantılar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      fetchMeetings();
    }, 30_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldCreate = params.get('create') === 'true';
    const fromCalendar = params.get('from') === 'calendar';
    const dateParam = params.get('date') ?? undefined;
    const timeParam = params.get('time') ?? undefined;

    if (shouldCreate) {
      setShowCreateForm(true);
      setReturnToCalendar(fromCalendar);

      if (dateParam) {
        setPrefillDate(dateParam);
        setPrefillTime(timeParam ?? getDefaultMeetingTime(new Date(`${dateParam}T12:00:00`)));
      } else {
        setPrefillDate(undefined);
        setPrefillTime(undefined);
      }
    }
  }, [location]);

  const handleCloseForm = () => {
    setShowCreateForm(false);
    setPrefillDate(undefined);
    setPrefillTime(undefined);
    setReturnToCalendar(false);
    window.history.replaceState({}, '', '/plans');
    fetchMeetings();
  };

  const handleMeetingCreated = (scheduledAt?: string) => {
    if (returnToCalendar && scheduledAt) {
      const date = scheduledAt.slice(0, 10);
      navigate(`/calendar?date=${date}`);
      return;
    }
    handleCloseForm();
  };

  const handleDeleteMeeting = async (id: string) => {
    try {
      await meetingService.deleteMeeting(id);
      fetchMeetings();
    } catch (error) {
      console.error('Toplantı silinemedi:', error);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Calendar}
        title="Toplantı Planlama"
        description="Planlı toplantılarınızı oluşturun ve yönetin."
        actions={
          !showCreateForm ? (
            <button type="button" onClick={() => setShowCreateForm(true)} className={pageHeaderPrimaryButtonClass}>
              <Plus className="w-5 h-5" />
              <span>Yeni Toplantı</span>
            </button>
          ) : undefined
        }
      />

      {/* Form */}
      {showCreateForm && (
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4 sm:p-6">
          <CreateMeetingForm
            onClose={handleCloseForm}
            onCreated={handleMeetingCreated}
            initialDate={prefillDate}
            initialTime={prefillTime}
            lockDate={returnToCalendar && Boolean(prefillDate)}
          />
        </div>
      )}

      {/* Liste veya Boş */}
      {!showCreateForm && (
        loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        ) : meetings.length === 0 ? (
          <EmptyMeetingsState onCreateClick={() => setShowCreateForm(true)} />
        ) : (
          <ScheduledMeetingsList meetings={meetings} onDelete={handleDeleteMeeting} onRefresh={fetchMeetings} />
        )
      )}
    </div>
  );
}

function EmptyMeetingsState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 sm:p-8 lg:p-12">
      <div className="max-w-lg mx-auto text-center">
        <div className="w-20 h-20 lg:w-24 lg:h-24 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Video className="w-10 h-10 lg:w-12 lg:h-12 text-purple-400" />
        </div>
        <h2 className="text-xl lg:text-2xl font-bold text-white mb-3">Henüz Toplantı Yok</h2>
        <p className="text-gray-400 text-sm lg:text-base mb-8">
          İlk toplantınızı oluşturarak ekibinizle iletişime geçin.
        </p>
        <div className="grid grid-cols-3 gap-3 lg:gap-4 mb-8">
          <div className="bg-white/5 rounded-xl p-3 lg:p-4">
            <Video className="w-6 h-6 lg:w-8 lg:h-8 text-purple-400 mx-auto mb-2" />
            <p className="text-white text-xs lg:text-sm font-medium">HD Video</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 lg:p-4">
            <Clock className="w-6 h-6 lg:w-8 lg:h-8 text-purple-400 mx-auto mb-2" />
            <p className="text-white text-xs lg:text-sm font-medium">50 Dakika</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 lg:p-4">
            <Calendar className="w-6 h-6 lg:w-8 lg:h-8 text-purple-400 mx-auto mb-2" />
            <p className="text-white text-xs lg:text-sm font-medium">Kolay</p>
          </div>
        </div>
        <button
          onClick={onCreateClick}
          className="px-6 py-2.5 lg:px-8 lg:py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold transition transform hover:scale-105 flex items-center justify-center gap-2 mx-auto"
        >
          <Plus className="w-5 h-5" />
          <span>İlk Toplantınızı Oluşturun</span>
        </button>
      </div>
    </div>
  );
}

export default Plans;